import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { isMobileOnly } from 'react-device-detect';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';

import useCountries from '../../hooks/useCountries';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import { useApplicantData } from '../../hooks/useApplicantData';
import { findCountryLabelByValue } from '../../util/helpers/countries';
import {
  iraCountryOfCitizenshipButtonDisable,
  iraCountryOfCitizenshipNote,
} from '../../util/helpers/iraAccountHelpers';
import {
  utmaCountryOfCitizenshipButtonDisable,
  utmaCountryOfCitizenshipNote,
} from '../../util/helpers/utmaAccountHelper';

import useStepPost from '../../hooks/useStepPost';
import useJointApplicationOwnerCountries from '../../hooks/useJointApplicationOwnerCountries';
import {
  APPLICANT_TYPES,
  BASIC_ACCOUNT_UPGRADE_TYPES,
  USA_COUNTRY_LABEL,
  APPLICATION_TYPE,
} from '../../util/constants';
import usePersonIdentityCountryDispatch from '../../hooks/usePersonIdentityCountryDispatch';
import { FormWrap } from '../FormContainer/FormContainer';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import Select from '../Fields/Select';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { selectBasicAccount } from '../../store/selectors/userSelectors';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  BASIC_ACCOUNT_QUERY_DATA,
  // IS_SALES_ASSESSTED_REGISTRATION_FLOW,
} from '../../constants/sessionStorage';
import { getBasicAccount } from '../../store/actions/user/userActions';
import useApplicationUid from '../../hooks/useApplicationUid';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import {
  APPLICATION_TYPE_IRA,
  APPLICATION_TYPE_UTMA,
} from '../../constants/accountTypes';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';

const IdentityCountryForm = ({
  nextPath,
  skipStepPath,
  dependentSkipStepPath,
  applicantType,
  onModalClose,
  formId,
  setEventType,
  eventType,
}) => {
  const { t } = useTranslation();
  const countries = useCountries();
  const dispatch = useDispatch();
  const { ApplicationUid } = useApplicationUid();
  const ApplicantData = useApplicantData(applicantType);
  const [skipPath, setSkipPath] = useState('');
  const { mainIdentityCountry } = useJointApplicationOwnerCountries();
  const { personIdentityCountryDispatch } = usePersonIdentityCountryDispatch();
  const { BrokerCode } = useSelector(selectSettings);
  const basicAccountData = useSelector(selectBasicAccount);
  const basicAccountQueryData = retrieveFromSessionStorage(
    BASIC_ACCOUNT_QUERY_DATA,
  );
  const Application = useSelector(selectApplication);
  const { AccountUpgradeType } = Application;
  const isUpgrade =
    AccountUpgradeType === BASIC_ACCOUNT_UPGRADE_TYPES.IN_PROCESS;

  const defaultAddress = basicAccountData?.Addresses?.find(
    (element) => element.IsDefaultMailingAddress === true,
  );
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const addressCountryIso3Code =
    applicantType === APPLICANT_TYPES.MAIN && defaultAddress?.CountryIso3Code
      ? defaultAddress?.CountryIso3Code
      : '';
  // const isSalesAssisted = retrieveFromSessionStorage(
  //   IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  // );

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (
      applicantType === APPLICANT_TYPES.MAIN &&
      ApplicantData?.PersonName &&
      ApplicantData?.PersonBirthday?.DateOfBirth &&
      !isEmpty(ApplicantData?.PersonBirthday?.DateOfBirth)
    ) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Secure Account Country Identity',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
      });
      return;
    }
    if (
      ApplicantData?.PersonName &&
      ApplicantData?.PersonBirthday?.DateOfBirth &&
      !isEmpty(ApplicantData?.PersonBirthday?.DateOfBirth)
    ) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Secure Account Country Identity Second Owner',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ApplicantData?.PersonBirthday?.DateOfBirth]);

  const handleGtmEvent = () => {
    if (applicantType === APPLICANT_TYPES.MAIN) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_COMPLETE,
        stepLabel: 'Secure Account Country Identity',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
      });
      return;
    }
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Secure Account Country Identity Second Owner',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
    });
  };

  useEffect(() => {
    const { ApplicationType } = Application;
    if (
      basicAccountQueryData?.accountUid &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_UTMA &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_IRA
    ) {
      dispatch(
        getBasicAccount({
          applicationUid: ApplicationUid,
          accountUid: basicAccountQueryData?.accountUid,
        }),
      );
      return;
    }
    if (
      isUpgrade &&
      Application?.AccountUid &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_UTMA &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_IRA
    ) {
      dispatch(
        getBasicAccount({
          applicationUid: ApplicationUid,
          accountUid: Application?.AccountUid,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedCountyField, setSelectedCountryField] = useState('USA');

  const COUNTRY_OPTIONS = [
    ...countries.map((country) => ({
      value: country.Iso3Code,
      label: country.Caption,
      dataCy: 'menu-item-country',
    })),
  ];

  const formValidationSchema = Yup.object({
    ...getValidationSchema({
      countries: countries?.map((country) => ({
        label: country?.label?.toLowerCase(),
      })),
      fields: ['IdentityCountry'],
    }),
  });

  const defaultValues = {
    IdentityCountry: {
      value:
        getApplicantData(ApplicantData, 'PersonCountry', 'CountryIso3Code') ||
        addressCountryIso3Code,
      label:
        findCountryLabelByValue(
          countries,
          getApplicantData(ApplicantData, 'PersonCountry', 'CountryIso3Code') ||
            addressCountryIso3Code,
        ) || null,
    },
  };

  const { handleSubmit, setError, control, watch, setValue } = useForm({
    resolver: yupResolver(formValidationSchema),
    defaultValues: {
      IdentityCountry: {
        value: defaultValues.value || 'USA',
        label: defaultValues.label || USA_COUNTRY_LABEL,
      },
    },
  });

  const selectedCountry = watch('IdentityCountry');

  const getSkipPath = () => {
    if (applicantType === APPLICANT_TYPES.SECONDARY) {
      if (mainIdentityCountry !== 'USA' && selectedCountry.value !== 'USA') {
        setSkipPath(skipStepPath);
        return;
      }

      setSkipPath(dependentSkipStepPath);
      return;
    }
    setSkipPath(skipStepPath);
  };

  useEffect(() => {
    getSkipPath();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  const { onSuccess, onSkipStep } = useStepPost({
    nextPath,
    skipStepPath: skipPath,
    onModalClose,
  });

  useEffect(() => {
    if (countries.length > 0) {
      const value =
        getApplicantData(ApplicantData, 'PersonCountry', 'CountryIso3Code') ||
        addressCountryIso3Code ||
        'USA';
      const label =
        findCountryLabelByValue(
          countries,
          getApplicantData(ApplicantData, 'PersonCountry', 'CountryIso3Code') ||
            addressCountryIso3Code,
        ) || USA_COUNTRY_LABEL;
      setValue('IdentityCountry', {
        value,
        label,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries]);

  useEffect(() => {
    setSelectedCountryField(selectedCountry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  const onRequestSuccess = () => {
    if (selectedCountry.value !== 'USA') {
      onSkipStep();
      return;
    }

    onSuccess();
  };
  const onSubmit = ({ IdentityCountry }) => {
    if (!ApplicantData?.PersonUid || !IdentityCountry) {
      return;
    }

    const onError = (message) => {
      setError('IdentityCountry', {
        type: 'string',
        message,
      });
    };

    personIdentityCountryDispatch({
      personUid: ApplicantData?.PersonUid,
      data: {
        IdentityCountry,
      },
      defaultValues,
      onSuccess:
        eventType === 'Form'
          ? () => onRequestSuccess()
          : () => {
              setHasError(false);
              setSaveAndCloseModalOpen(true);
            },
      onError:
        eventType === 'Form'
          ? () => onError()
          : () => {
              setHasError(true);
              setSaveAndCloseModalOpen(true);
            },
    });
    handleGtmEvent();
  };

  return (
    <FormWrap id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        render={({ field: { ref, ...field }, fieldState }) => {
          setSelectedCountryField(field?.value.value);
          return (
            <Select
              width={isMobileOnly ? '100%' : '90%'}
              options={COUNTRY_OPTIONS}
              {...field}
              {...fieldState}
              autoFocus
              dataCyButtonToggle="button-toggle-country-list"
            />
          );
        }}
        name="IdentityCountry"
      />
      {iraCountryOfCitizenshipNote(selectedCountyField, BrokerCode)}
      {utmaCountryOfCitizenshipNote(selectedCountyField, BrokerCode)}
      <OnBoardingCta
        type="submit"
        onClick={() => setEventType('Form')}
        title={nextPath ? t('continue') : t('update')}
        disabled={
          iraCountryOfCitizenshipButtonDisable(selectedCountyField) ||
          utmaCountryOfCitizenshipButtonDisable(selectedCountyField)
        }
        buttonProps={{
          'data-cy': nextPath ? 'button-continue' : 'button-update',
        }}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          hasError={hasError}
          onSuccess={() => onRequestSuccess()}
        />
      )}
    </FormWrap>
  );
};

IdentityCountryForm.propTypes = {
  nextPath: PropTypes.string,
  applicantType: PropTypes.string,
  onModalClose: PropTypes.func,
  skipStepPath: PropTypes.string,
  dependentSkipStepPath: PropTypes.string,
  formId: PropTypes.string,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
};

export default IdentityCountryForm;
